import { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Page, Reveal, MaskedLines, EASE } from '../components/motion';
import { ProjectCard } from '../components/ProjectCard';
import { CTASection } from '../components/CTASection';
import { PROJECTS } from '../data/content';
import { useSEO } from '../hooks/useSEO';

const FILTERS = ['All', 'Completed', 'On-Going'];

export default function Projects() {
  useSEO('Projects', 'Completed and on-going building construction projects by Ehimo Building & Civil Engineering — Lekki, Lagos Island, Ilorin, Uyo, Ikeja, Ado-Ekiti, Ikorodu and more.');
  const [filter, setFilter] = useState('All');

  const visible = useMemo(
    () => (filter === 'All' ? PROJECTS : PROJECTS.filter((p) => p.status === filter)),
    [filter]
  );

  return (
    <Page>
      <section className="pt-32 pb-10 lg:pt-40 lg:pb-12 border-b border-line relative overflow-hidden">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <Reveal y={14} className="mb-6">
            <p className="text-xs uppercase tracking-[0.3em] text-accent">Our projects</p>
          </Reveal>
          <MaskedLines
            lines={['Completed &', 'on-going works.']}
            delay={0.1}
            lineClassName="font-display text-3xl sm:text-5xl lg:text-6xl font-semibold uppercase tracking-tight leading-[0.95]"
          />
          <Reveal delay={0.4} className="mt-5 flex flex-col sm:flex-row sm:items-center justify-between gap-6">
            <p className="text-mute max-w-xl leading-relaxed">
              Building construction projects delivered across Nigeria — from Lagos to Ilorin, Uyo and Ado-Ekiti.
            </p>
            <p className="font-display text-sm text-mute uppercase tracking-[0.2em]" data-testid="project-count">
              {visible.length} {visible.length === 1 ? 'project' : 'projects'}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-paper text-ink">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-10 lg:py-12">
        <div className="flex flex-wrap gap-3 mb-8" role="group" aria-label="Filter projects by status">
          {FILTERS.map((f) => (
            <button
              key={f}
              data-testid={`filter-${f.toLowerCase().replace(/[^a-z]/g, '-')}`}
              onClick={() => setFilter(f)}
              aria-pressed={filter === f}
              className={`text-xs uppercase tracking-[0.2em] px-6 py-3 border transition-colors duration-300 ${
                filter === f
                  ? 'bg-accent text-ink border-accent font-semibold'
                  : 'border-paperline text-inkmute hover:border-ink hover:text-ink bg-white'
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="projects-grid">
          <AnimatePresence mode="popLayout">
            {visible.map((p, i) => (
              <motion.div
                key={p.slug}
                layout
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.97 }}
                transition={{ duration: 0.5, delay: i * 0.04, ease: EASE }}
              >
                <ProjectCard project={p} index={i} />
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
        </div>
      </section>

      <CTASection title={['Your project', 'could be next.']} />
    </Page>
  );
}
